import {
  createGrid,
  GridOptions,
  ColDef,
  EditableCallbackParams,
  CellClassParams,
  ProcessDataFromClipboardParams,
  CellValueChangedEvent,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { ebEditableTheme } from "../ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../ag_grid_shared/filter_defaults";
import { NO_CLIENT_EXPORT } from "../ag_grid_shared/no_client_export";

// ─── Per-cell contract ──────────────────────────────────────────────────────
// Row data is keyed by column id (see VoterList::Update::Status::GridRowsBuilder),
// each cell shaped exactly as VotersAttributesGetter#select_attributes emits it.
interface EditableCell {
  value: string | number | null;
  read_only: boolean;
}

type EditableRow = Record<string, EditableCell | undefined>;

const LOCKED_CELL_CLASS = "ag-grid-editable-cell--locked";
const LOCKED_ROW_CLASS = "ag-grid-editable-cell--row-locked";

/**
 * Client-side, inline-editable AG-Grid controller for fixed-roster bulk-edit
 * pages (e.g. "Update Voters"). No sort/filter/row-drag/column-drag — the
 * backend matches submitted rows to records by positional array index only,
 * so display order must always equal load order (see class comment on the
 * Ruby component for the exact services this constraint comes from).
 *
 * This controller intentionally does NOT know about any particular page's
 * routes: the Validate button carries its own POST url in
 * `data-url`, and the filter (invitation) ids are read from the existing
 * `#invitations_array` hidden fields — exactly how
 * app/assets/javascripts/controllers/update_voters.coffee did it. That keeps
 * this controller reusable for any future fixed-roster editable grid.
 */
export default class extends AgGridBaseController {
  static values = {
    ...AgGridBaseController.values,
    rowData: Array,
  };

  declare readonly rowDataValue: EditableRow[];

  private validateClickHandler: ((event: Event) => void) | null = null;

  connect() {
    super.connect(); // license key + outside click handler

    if (!this.hasGridTarget) return;

    const options: GridOptions = {
      theme: ebEditableTheme,
      localeText: ebLocaleText,
      columnDefs: this.buildEditableColumnDefs(this.columnDefsValue),
      rowData: this.rowDataValue,
      domLayout: "autoHeight",
      suppressMovableColumns: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowDrag: true,
      rowGroupPanelShow: "never",
      cellSelection: true,
      // Without this, an uncommitted mid-edit cell never reaches node.data, so
      // gatherGridData() below would silently read the stale pre-edit value.
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      processDataFromClipboard: (params: ProcessDataFromClipboardParams) =>
        this.clampPasteToGrid(params),
      onFirstDataRendered: () => this.autoSizeNonFlexColumns(),
      // Re-fit columns whose content grew past the initial autosize once an edit commits.
      onCellValueChanged: (_event: CellValueChangedEvent) => this.autoSizeNonFlexColumns(),
      // Menu popups render outside the grid's own (short/narrow) bounding box instead
      // of being clipped by it.
      popupParent: document.body,
      getMainMenuItems: (params) => this.resetColumnsOnlyMainMenuItems(params),
      defaultColDef: {
        sortable: false,
        resizable: true,
        suppressMovable: true,
        lockPinned: true,
        // Right-click still opens the (now Reset-Columns-only) menu; only the
        // always-visible 3-dot button is removed.
        suppressHeaderMenuButton: true,
        filterParams: ebFilterParams,
      },
      ...this.gridOptionsValue,
      ...NO_CLIENT_EXPORT,
    };

    this.gridApi = createGrid(this.gridTarget, options);

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      this.gridApi?.clearCellSelection();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);

    this.wireValidateButton();
  }

  disconnect() {
    if (this.validateClickHandler) {
      const validateButton = document.getElementById("validate_list");
      validateButton?.removeEventListener("click", this.validateClickHandler);
      this.validateClickHandler = null;
    }
    super.disconnect();
  }

  // ── Abstract overrides — this page has no basic/advanced toggle ──────────

  switchToBasic(): void {
    // no-op: view_toggle is never enabled for this component
  }

  switchToAdvanced(): void {
    // no-op: view_toggle is never enabled for this component
  }

  // ── Editable / read-only wiring ───────────────────────────────────────────

  private buildEditableColumnDefs(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((colDef) => {
      const colId = colDef.colId ?? (typeof colDef.field === "string" ? colDef.field.split(".")[0] : undefined);
      return {
        ...colDef,
        editable: (params: EditableCallbackParams) => this.isCellEditable(params.data as EditableRow, colId),
        cellClassRules: {
          [LOCKED_CELL_CLASS]: (params: CellClassParams) => !this.isCellEditable(params.data as EditableRow, colId),
          [LOCKED_ROW_CLASS]: (params: CellClassParams) => this.isRowFullyLocked(params.data as EditableRow),
        },
      };
    });
  }

  // Defaults to non-editable when the read_only signal is missing or unclear
  // — never default-open.
  private isCellEditable(data: EditableRow | undefined, colId: string | undefined): boolean {
    if (!data || !colId) return false;
    const cell = data[colId];
    if (!cell || typeof cell.read_only !== "boolean") return false;
    return !cell.read_only;
  }

  private isRowFullyLocked(data: EditableRow | undefined): boolean {
    if (!data) return false;
    const cells = Object.values(data).filter((cell): cell is EditableCell => !!cell);
    if (cells.length === 0) return false;
    return cells.every((cell) => cell.read_only === true);
  }

  // ── Paste clamp ────────────────────────────────────────────────────────────
  // A pasted block must never extend past the last row — this grid's roster is fixed
  // (no row id in the submit payload), so AG-Grid can't insert new rows.
  private clampPasteToGrid(params: ProcessDataFromClipboardParams): string[][] | null {
    if (!this.gridApi) return params.data;
    const focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) return params.data;

    const totalRows = this.gridApi.getDisplayedRowCount();
    const availableRows = totalRows - focusedCell.rowIndex;
    if (availableRows <= 0) return null;

    return params.data.length > availableRows ? params.data.slice(0, availableRows) : params.data;
  }

  // ── Validate / Update state machine (mirrors update_voters.coffee) ───────

  private wireValidateButton(): void {
    const validateButton = document.getElementById("validate_list");
    if (!validateButton) return;

    this.validateClickHandler = (event: Event) => {
      event.preventDefault();
      this.handleValidateClick(validateButton);
    };
    validateButton.addEventListener("click", this.validateClickHandler);
  }

  private handleValidateClick(validateButton: HTMLElement): void {
    // Defense-in-depth alongside stopEditingWhenCellsLoseFocus: force any in-progress
    // edit to commit before reading node.data.
    this.gridApi?.stopEditing();

    validateButton.classList.add("disabled");
    validateButton.innerHTML = "Validating rows, please wait...";

    const { headers, rows } = this.gatherGridData();
    const filterIds = this.gatherFilterIds();

    this.postValidate(validateButton, headers, rows, filterIds)
      .then((data: { valid: boolean }) => this.handleValidateResponse(data, validateButton))
      .catch(() => this.handleValidateFailure(validateButton));
  }

  private gatherGridData(): { headers: string[]; rows: string[][] } {
    const headers = this.columnDefsValue.map((colDef) => {
      const field = typeof colDef.field === "string" ? colDef.field : "";
      return colDef.colId ?? field.split(".")[0];
    });

    const rows: string[][] = [];
    // forEachNode ignores filtering/sorting (ag-grid-community gridApi.d.ts) — load
    // order is preserved regardless of on-screen state.
    this.gridApi?.forEachNode((node) => {
      const data = node.data as EditableRow | undefined;
      if (!data) return;
      rows.push(
        headers.map((colId) => {
          const cell = data[colId];
          return cell?.value === undefined || cell?.value === null ? "" : String(cell.value);
        })
      );
    });

    return { headers, rows };
  }

  private gatherFilterIds(): string[] {
    return Array.from(document.querySelectorAll<HTMLInputElement>("#invitations_array input")).map(
      (input) => input.value
    );
  }

  private postValidate(
    validateButton: HTMLElement,
    headers: string[],
    rows: string[][],
    filterIds: string[]
  ): Promise<{ valid: boolean }> {
    const url = validateButton.dataset.url;
    if (!url) return Promise.reject(new Error("Validate button is missing data-url"));

    const body = new URLSearchParams();
    headers.forEach((colId) => body.append(`headers[${colId}]`, ""));
    rows.forEach((row, index) => {
      row.forEach((cell) => body.append(`rows[${index}][]`, cell));
    });
    filterIds.forEach((id) => body.append("filter[]", id));

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: body.toString(),
    }).then((response) => {
      if (!response.ok) throw new Error(`Validate request failed: ${response.status}`);
      return response.json();
    });
  }

  private handleValidateResponse(data: { valid: boolean }, validateButton: HTMLElement): void {
    const notices = document.getElementById("voter_list_notices");
    const downloadList = document.getElementById("download_list");
    const uploadList = document.getElementById("upload_list");
    const updateReason = document.getElementById("update-reason") as HTMLTextAreaElement | null;

    const listWarnMessage =
      "There are errors in your list that must be fixed before continuing. To view the errors and explanations, click Download List. Then, fix the errors, and verify by selecting Validate List again.";
    const reasonWarnMessage = "Edit Reason is required";

    validateButton.classList.remove("disabled");
    validateButton.innerHTML = '<i class="fas fa-check"></i>Validate list';

    const errors: string[] = [];
    if (!(updateReason?.value ?? "").trim().length) errors.push(reasonWarnMessage);

    if (data.valid) {
      downloadList?.classList.add("disabled");
    } else {
      downloadList?.classList.remove("disabled");
      errors.push(listWarnMessage);
    }

    if (!notices) return;

    if (errors.length > 0) {
      uploadList?.classList.add("disabled");
      notices.innerHTML = errors.map((message) => `<span class="inline_error">${message}</span><br>`).join("");
    } else {
      uploadList?.classList.remove("disabled");
      notices.innerHTML = '<span class="inline_success">Everything looks good!</span>';
    }
  }

  private handleValidateFailure(validateButton: HTMLElement): void {
    validateButton.classList.remove("disabled");
    validateButton.innerHTML = '<i class="fas fa-check"></i>Validate list';
    const notices = document.getElementById("voter_list_notices");
    if (notices) {
      notices.innerHTML =
        '<span class="inline_error">Something went wrong validating your list. Please try again.</span>';
    }
  }
}
